import {Button, Dropdown, DropdownItem, Tag, Tooltip} from 'platform/components';
import {HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {useController, UseFormReturn} from 'react-hook-form';

import i18n from '@dms/i18n';
import {
  getRecordAuditPairResourceLabel,
  RECORD_AUDIT_PAIR_RESOURCE_IDS,
  useLicensedRecordAuditPairResources,
} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {RecordAuditDefinitionFormValue} from '../types/RecordAuditDefinitionFormValue';

export interface ResourcesFieldProps extends TestIdProps {
  formApi: UseFormReturn<RecordAuditDefinitionFormValue>;
}

export function ResourcesField(props: ResourcesFieldProps) {
  // The definition can only target resources the audit runtime can pair with —
  // one shared list drives both this picker and the pair-record dialog. Built at
  // render time so labels follow the active language.
  const resourceOptions = RECORD_AUDIT_PAIR_RESOURCE_IDS.map((resourceId) => ({
    value: resourceId,
    label: getRecordAuditPairResourceLabel(resourceId),
  }));

  const {field} = useController({control: props.formApi.control, name: 'resourceIds'});
  const selected: string[] = field.value ?? [];

  // Chips for already-selected values stay visible (a definition may pre-date a
  // license change), but only licensed resources are offered for adding.
  const licensedResourceIds = useLicensedRecordAuditPairResources();
  const remainingOptions = resourceOptions.filter(
    (option) => !selected.includes(option.value) && licensedResourceIds.includes(option.value)
  );

  const labelFor = (value: string) =>
    resourceOptions.find((option) => option.value === value)?.label ?? value;

  return (
    <VStack spacing={1}>
      <HStack spacing={1} align="center">
        <Text size="xSmall" color="secondary">
          {i18n.t('page.settings.recordAuditDefinition.resources')}
        </Text>
        <Tooltip label={i18n.t('page.settings.generalChecklist.resourcesTooltip')}>
          <Icon value="action/info_outline" size={3} color="text.secondary" />
        </Tooltip>
      </HStack>
      <HStack spacing={2} align="center" wrap>
        {selected.map((value) => (
          <Tag
            key={value}
            label={labelFor(value)}
            colorScheme="neutral"
            isSubtle
            onDelete={() => field.onChange(selected.filter((item) => item !== value))}
            data-testid={suffixTestId(`resourceChip-${value}`, props)}
          />
        ))}
        <Show when={remainingOptions.length > 0}>
          <Dropdown
            placement="bottom-start"
            data-testid={suffixTestId('addResource', props)}
            dropdownControl={
              <Button
                variant="outlined"
                size="small"
                title={i18n.t('page.settings.generalChecklist.addResource')}
              />
            }
          >
            {remainingOptions.map((option) => (
              <DropdownItem
                key={option.value}
                label={option.label}
                onClick={() => field.onChange([...selected, option.value])}
              />
            ))}
          </Dropdown>
        </Show>
      </HStack>
    </VStack>
  );
}
